import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronLeft, ChevronRight, Calendar, MapPin, User, 
  Building2, Download, Share2, X, ZoomIn, ExternalLink,
  Clock, CheckCircle2, Circle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/portfolio-facts.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


// SV-2 — a scan's totals, and its own room schedule as reference. Project
// totals only: a remodel changes, removes and adds rooms, so nothing here is
// compared room by room, and none of it is a designed space or a quantity.
const DELTA_WORDS = [
  ['finished_sqft_delta', 'sq ft finished'],
  ['unfinished_sqft_delta', 'sq ft unfinished'],
  ['rooms_delta', 'room'],
  ['bedrooms_delta', 'bedroom'],
  ['bathrooms_delta', 'bathroom'],
  ['floors_delta', 'floor'],
];

export const changeWords = (change) => DELTA_WORDS
  .filter(([key]) => Number(change?.[key] || 0) !== 0)
  .map(([key, word]) => {
    const n = Number(change[key]);
    const sign = n > 0 ? '+' : '\u2212';
    const size = Math.abs(n);
    const plural = word.startsWith('sq ft') || size === 1 ? word : `${word}s`;
    return `${sign}${size.toLocaleString()} ${plural}`;
  })
  .join(' \u00b7 ');

const ScanTotals = ({ block, heading, empty, which }) => {
  const [open, setOpen] = useState(false);
  if (!block) {
    return (
      <div className="kpf-ba__c" data-testid={`portfolio-scan-${which}-empty`}>
        <h3 className="kpf-ba__h">{heading}</h3>
        <p className="kpf-ba__e">{empty}</p>
      </div>
    );
  }
  const count = (n, word) => (n ? `${n} ${n === 1 ? word : `${word}s`}` : '');
  const rooms = [count(block.rooms, 'room'), count(block.bedrooms, 'bedroom'),
    count(block.bathrooms, 'bathroom')].filter(Boolean).join(' \u00b7 ');
  return (
    <div className="kpf-ba__c" data-testid={`portfolio-scan-${which}`}>
      <h3 className="kpf-ba__h">{heading}</h3>
      <p className="kpf-ba__m">
        {[block.label, block.fetched_at ? new Date(block.fetched_at).toLocaleDateString() : '',
          block.provider].filter(Boolean).join(' \u00b7 ')}
      </p>
      <dl className="kpf-ba__g">
        {[['Finished', block.finished_sqft ? `${block.finished_sqft.toLocaleString()} sq ft` : ''],
          ['Floors', block.floors || ''],
          ['Rooms', rooms],
          ['Ceiling', block.ceiling_ftin_common || '']].filter(([, v]) => v).map(([k, v]) => (
            <div key={k}>
              <dt className="kpf-ba__k">{k}</dt>
              <dd className="kpf-ba__v">{v}</dd>
            </div>
          ))}
      </dl>
      {block.tour_url && (
        <a className="kpf-ba__a" href={block.tour_url} target="_blank" rel="noopener noreferrer"
          data-testid={`portfolio-scan-${which}-tour`}>Walk the house as it was</a>
      )}
      {(block.room_schedule || []).length > 0 && (
        <div className="kpf-ba__s">
          <button type="button" className="kpf-ba__t" onClick={() => setOpen((o) => !o)}
            data-testid={`portfolio-scan-${which}-schedule-toggle`}>
            Room schedule — {block.room_schedule.length} rooms as measured {open ? '\u2212' : '+'}
          </button>
          {open && (
            <table className="kpf-ba__tb" data-testid={`portfolio-scan-${which}-schedule`}>
              <tbody>
                {block.room_schedule.map((r, i) => (
                  <tr key={`${r.name}-${i}`}>
                    <td>{r.name}</td>
                    <td>{r.floor}</td>
                    <td>{r.size}</td>
                    <td>{r.area_sqft ? `${r.area_sqft} sq ft` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default function PortfolioViewer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/portfolio/${token}`);
        setPortfolio(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('This portfolio link is invalid or has been removed.');
        } else if (err.response?.status === 410) {
          setError('This portfolio link has expired.');
        } else {
          setError('Unable to load portfolio. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPortfolio();
    }
  }, [token]);

  const nextPhoto = () => {
    if (portfolio?.photos?.length) {
      setCurrentPhotoIndex((prev) => (prev + 1) % portfolio.photos.length);
    }
  };

  const prevPhoto = () => {
    if (portfolio?.photos?.length) {
      setCurrentPhotoIndex((prev) => (prev - 1 + portfolio.photos.length) % portfolio.photos.length);
    }
  };

  const brandColor = portfolio?.settings?.branding_color || '#BC5D41';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: brandColor, borderTopColor: 'transparent' }} />
          <p className="text-gray-400">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Portfolio Unavailable</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline" className="border-gray-700 text-gray-300">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  if (!portfolio) return null;

  const { project, photos, timeline, settings, site_facts: siteFacts } = portfolio;

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-gray-800 bg-[#0D0D0D]/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: brandColor }}
            >
              {project.name?.charAt(0) || 'P'}
            </div>
            <div>
              <h1 className="font-semibold text-sm">{project.name}</h1>
              <p className="text-xs text-gray-400">{project.client_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied!');
              }}
              className="text-gray-400 hover:text-white"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        {project.cover_image ? (
          <img 
            src={project.cover_image} 
            alt={project.name}
            className="w-full h-full object-cover"
          />
        ) : photos?.[0]?.image_url ? (
          <img 
            src={photos[0].image_url} 
            alt={project.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div 
            className="w-full h-full"
            style={{ background: `linear-gradient(135deg, ${brandColor}22 0%, ${brandColor}44 100%)` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-8 max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-wider mb-2" style={{ color: brandColor }}>
              {project.project_type || 'Design Project'}
            </p>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{project.name}</h1>
            {project.client_name && (
              <p className="text-xl text-gray-300 mb-4">for {project.client_name}</p>
            )}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
              {project.address && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {project.address}
                </span>
              )}
              {(project.start_date || project.end_date) && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {project.start_date && new Date(project.start_date).getFullYear()}
                  {project.end_date && ` - ${new Date(project.end_date).getFullYear()}`}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      {(project.description || settings.custom_intro) && (
        <section className="py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-lg text-gray-300 leading-relaxed">
              {settings.custom_intro || project.description}
            </p>
          </div>
        </section>
      )}

      {/* Photo Gallery */}
      {photos && photos.length > 0 && (
        <section className="py-16 px-4 bg-[#111]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
              <span className="w-1 h-8 rounded-full" style={{ backgroundColor: brandColor }} />
              Project Gallery
            </h2>
            
            {/* Featured Photo */}
            <div 
              className="relative aspect-video rounded-xl overflow-hidden mb-4 cursor-pointer group"
              onClick={() => setShowLightbox(true)}
            >
              <img 
                src={photos[currentPhotoIndex]?.image_url || photos[currentPhotoIndex]?.thumbnail_url} 
                alt={photos[currentPhotoIndex]?.title || 'Project photo'}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              
              {/* Navigation arrows */}
              {photos.length > 1 && (
                <>
                  <button 
                    onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
              
              {/* Photo counter */}
              <div className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-black/50 text-sm">
                {currentPhotoIndex + 1} / {photos.length}
              </div>
            </div>
            
            {/* Thumbnails */}
            {photos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {photos.map((photo, idx) => (
                  <button
                    key={photo.id || idx}
                    onClick={() => setCurrentPhotoIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      idx === currentPhotoIndex ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={photo.thumbnail_url || photo.image_url} 
                      alt={photo.title || `Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* SC-5 — Site & property: the facts the site record keeps current. */}
      {siteFacts && settings.include_site_facts && (
        <section className="py-12 px-4" data-testid="portfolio-site-facts">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
              <span className="w-1 h-8 rounded-full" style={{ backgroundColor: brandColor }} />
              Site &amp; property
            </h2>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[['Year built', siteFacts.year_built],
                ['Finished area', siteFacts.finished_sqft ? `${siteFacts.finished_sqft} sq ft` : ''],
                ['Lot', siteFacts.lot],
                ['Climate zone', siteFacts.climate_zone],
                ['Scan', siteFacts.scan_provider],
                ['Record', siteFacts.as_built_gathered]].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs uppercase tracking-wide text-gray-400">{k}</dt>
                    <dd className="text-lg">{v}</dd>
                  </div>
                ))}
            </dl>
            {siteFacts.before_tour_url && !siteFacts.scan_before?.tour_url && (
              <a className="inline-block mt-6 text-sm underline" href={siteFacts.before_tour_url}
                target="_blank" rel="noopener noreferrer" data-testid="portfolio-before-tour">
                Walk the house as it was, before the work
              </a>
            )}

            {/* SV-2 — the scan as what once was: totals before, totals after.
                The two room schedules are reference; they are never paired. */}
            {(siteFacts.scan_before || siteFacts.scan_after) && (
              <div className="kpf-ba" data-testid="portfolio-before-after">
                <ScanTotals block={siteFacts.scan_before} heading="Before"
                  empty="No pre-construction scan was measured" which="before" />
                <ScanTotals block={siteFacts.scan_after} heading="After"
                  empty="Final scan not yet captured" which="after" />
              </div>
            )}
            {siteFacts.scan_change && (
              <p className="kpf-ch" data-testid="portfolio-scan-change">
                <span className="kpf-ch__k">What changed</span>
                {' '}{changeWords(siteFacts.scan_change)}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Timeline */}
      {timeline && timeline.length > 0 && settings.include_timeline && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
              <span className="w-1 h-8 rounded-full" style={{ backgroundColor: brandColor }} />
              Project Timeline
            </h2>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-800" />
              
              {/* Timeline items */}
              <div className="space-y-8">
                {timeline.map((phase, idx) => (
                  <div key={idx} className="relative pl-12">
                    {/* Dot */}
                    <div 
                      className="absolute left-2 top-1 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                      style={{ 
                        borderColor: phase.completed ? brandColor : '#374151',
                        backgroundColor: phase.completed ? brandColor : 'transparent'
                      }}
                    >
                      {phase.completed && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </div>
                    
                    <div className={`${phase.completed ? '' : 'opacity-60'}`}>
                      <h3 className="font-semibold text-lg">{phase.name || `Phase ${idx + 1}`}</h3>
                      {phase.description && (
                        <p className="text-gray-400 mt-1">{phase.description}</p>
                      )}
                      {(phase.start_date || phase.end_date) && (
                        <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {phase.start_date && new Date(phase.start_date).toLocaleDateString()}
                          {phase.end_date && ` - ${new Date(phase.end_date).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: brandColor }}
            >
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm text-gray-400">
              Portfolio by KEY-MADE
            </span>
          </div>
          <p className="text-xs text-gray-500">
            {portfolio.view_count} views
          </p>
        </div>
      </footer>

      {/* Lightbox */}
      {showLightbox && photos && photos.length > 0 && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          <button 
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <X className="w-6 h-6" />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          
          <img 
            src={photos[currentPhotoIndex]?.image_url} 
            alt={photos[currentPhotoIndex]?.title}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <button 
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-sm">
            {currentPhotoIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
